import { createSelector } from 'reselect';
import REDUCER_NAME from './we-spin-reducer-name.js';
import {
	ENDED,
	CANCELLED,
	RUNNING,
	SESSION_OFFLINE_RESULT,
	BUY_IN_STATES,
	streamSorter
} from '../constants/we-spin-stream-states.js';
import { selectCurrentHrefLang } from '~/components/brand/ducks/index.js';
import { selectLicense } from '~/components/brand/ducks/selectors';
import { LICENSE_DGA } from '~/utils/license-utils';

const EMPTY_ARRAY = [];
const NOTIFICATION_TIME_THRESHOLD = 600000; // Ten minutes in milliseconds

export const selectNotificationsSeenByPlayer = state => state[REDUCER_NAME].notificationsSeenByPlayer;
export const selectIsAdNotificationsEnabled = state => state[REDUCER_NAME].isAdNotificationsEnabled;
export const selectAllSessions = state => state[REDUCER_NAME].sessions;

export const selectStreamViews = state =>
	state[REDUCER_NAME].sessions.reduce((streamViews, session) => streamViews.concat(session.streamViews), []) ||
	EMPTY_ARRAY;

export const selectSessionsLoaded = state => state[REDUCER_NAME].sessionsLoaded;

export const selectStreamView = ref => state => selectStreamViews(state).find(({ streamRef }) => streamRef === ref);

export const selectFinishGame = eventRef => state => state[REDUCER_NAME].finishGames[eventRef];

export const selectGameRoom = ref => state =>
	[state[REDUCER_NAME].currentGameRoom].find(({ streamRef }) => streamRef === ref);

export const selectDisplayName = state => state[REDUCER_NAME].displayName;
export const selectProfileFetched = state => state[REDUCER_NAME].fetchedProfile;

export const selectTemporaryBuyIn = streamRef => state => state[REDUCER_NAME].temporaryBuyIn[streamRef] || '';

export const selectPlayerBalanceInEventCurrency = streamRef => state =>
	state[REDUCER_NAME].playerBalanceInEventCurrency[streamRef] || '';
export const selectPlayersBoughtIntoSession = streamRef => state =>
	state[REDUCER_NAME].playersBoughtIntoSession[streamRef] || '';

export const selectUpcomingStreams = createSelector(selectStreamViews, streamViews => {
	return streamViews
		.filter(({ state }) => state !== CANCELLED && state !== ENDED)
		.filter(({ balanceCapMet }) => !balanceCapMet)
		.filter(({ playerCapMet }) => !playerCapMet)
		.sort((a, b) => new Date(a.start) - new Date(b.start));
});

export const selectUpcomingStreamFromStreamer = (size, streamerName) =>
	createSelector(selectUpcomingStreams, upcomingStreams => {
		return upcomingStreams
			.filter(s => s.streamerName === streamerName)
			.filter((_, i) => size === undefined || i < size);
	});

export const selectUpcomingStreamFromOtherStreamers = (size, streamerName) =>
	createSelector(selectUpcomingStreams, upcomingStreams => {
		return upcomingStreams
			.filter(s => s.streamerName !== streamerName)
			.filter((_, i) => size === undefined || i < size);
	});

export const selectParticipations = state => state[REDUCER_NAME].participations;

export const selectSessions = state =>
	state[REDUCER_NAME].sessions.filter(
		({ sessionState, joined, live }) => sessionState !== SESSION_OFFLINE_RESULT || joined || live
	);

export const selectPrivateSessions = state => state[REDUCER_NAME].privateSessions;

export const selectClips = state => state[REDUCER_NAME].clips;

export const selectUpcomingSessionsToShowNotificationFor = createSelector(
	[selectAllSessions, selectNotificationsSeenByPlayer, selectCurrentHrefLang],
	(sessions, notificationsSeenByPlayer, currentLanguage) => {
		return sessions
			.filter(s => s.streamerOnsiteAdNotificationEnabled)
			.filter(s => !notificationsSeenByPlayer.includes(s.sessionRef))
			.filter(s => s.streamViews.find(event => event.language === currentLanguage))
			.filter(s => s.sessionState !== SESSION_OFFLINE_RESULT)
			.filter(s => {
				const sessionStart = new Date(s.estimatedStartTime).getTime();
				const now = new Date().getTime();
				return sessionStart - now < NOTIFICATION_TIME_THRESHOLD && sessionStart - now > 0;
			})
			.sort((a, b) => new Date(a.estimatedStartTime) - new Date(b.estimatedStartTime));
	}
);

export const selectSession = sessionRef => state =>
	state[REDUCER_NAME].sessions.find(session => session.sessionRef === sessionRef) ||
	state[REDUCER_NAME].privateSessions.find(session => session.sessionRef === sessionRef);

export const selectIsSessionOnline = sessionRef =>
	createSelector(selectSession(sessionRef), session => {
		return Boolean(session?.live);
	});

export const selectIsSessionOnlineByStreamRef = streamRef =>
	createSelector(
		selectSessions,
		sessions =>
			sessions.find(session => session.streamViews.find(streamView => streamView.streamRef === streamRef)).live
	);

export const selectSessionForStreamRef = streamRef => state =>
	[...selectSessions(state), ...selectPrivateSessions(state)].find(({ streamViews }) =>
		streamViews.some(sv => sv.streamRef === streamRef)
	);

export const selectRunningEventForSession = sessionRef => state => {
	const session = state[REDUCER_NAME].sessions.find(s => s.sessionRef === sessionRef);
	return session && session.streamViews.find(({ state: s }) => s === RUNNING);
};

export const selectNextBuyInEventForSession = sessionRef => state => {
	const session = state[REDUCER_NAME].sessions.find(s => s.sessionRef === sessionRef);
	return session && session.streamViews.find(({ state: s }) => BUY_IN_STATES.includes(s));
};

export const selectDefaultEventForSession = sessionRef => state => {
	const session = state[REDUCER_NAME].sessions.find(s => s.sessionRef === sessionRef);
	return session && session.streamViews.sort(streamSorter).find(Boolean);
};
export const selectCarouselItems = state => state[REDUCER_NAME].carousel.items;
export const selectCarouselHydrated = state => state[REDUCER_NAME]?.carousel.hydrated;
export const selectHasMadeDeposit = state => state[REDUCER_NAME].hasMadeDeposit;
export const selectConfiguration = state => state[REDUCER_NAME].configuration;
const selectGames = state => state[REDUCER_NAME].availableGames;

export const selectAvailableGames = createSelector([selectGames], availableGames => availableGames.gamesList);
export const selectAvailableProviders = createSelector([selectGames], availableGames => availableGames.providers);

export const selectWeSpinSuggestedBuyInEnabled = state => selectLicense(state) !== LICENSE_DGA;
